// Comments Section Web Component
import { escapeHtml } from '../utils/html-utils.js';
import { buildGroupCommentUrl } from '../utils/group-permalink.js';

class CommentsSection extends HTMLElement {
  constructor() {
    super();
    this.postId = null;
    this.comments = [];
    this.isLoading = false;
    this.error = null;
    this.replyingTo = null;
    this.currentUser = null;
    this.lazyLoad = false;
    this.commentsLoaded = false;
    this.commentCount = 0;
    this.editingCommentId = null; // Track which comment is being edited
    this._reportInFlight = false;
  }

  static get observedAttributes() {
    return ['post-id', 'lazy-load', 'group-id'];
  }

  get groupId() {
    const raw = this.getAttribute('group-id');
    if (!raw) return null;
    const id = parseInt(raw, 10);
    return Number.isNaN(id) ? null : id;
  }

  isGroupMode() {
    return this.groupId !== null;
  }

  getCommentPermalink(commentId) {
    if (this.isGroupMode()) {
      return buildGroupCommentUrl({
        groupId: this.groupId,
        postId: this.postId,
        commentId
      });
    }
    return `/post/${this.postId}/comment/${commentId}`;
  }

  connectedCallback() {
    this.render();
    this.attachEventListeners();

    // Check if we should eagerly load comments
    const lazyLoad = this.getAttribute('lazy-load');
    if (lazyLoad === 'false' && this.postId && !this.commentsLoaded) {
      this.loadComments();
    }
  }

  disconnectedCallback() {
    this.removeEventListeners();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'post-id' && newValue !== oldValue) {
      this.postId = newValue;
      this.commentsLoaded = false;
      this.comments = [];
      this.updateContent();

      // If eager loading is enabled and we have a post ID, load comments immediately
      const lazyLoad = this.getAttribute('lazy-load');
      if (lazyLoad === 'false' && this.postId) {
        this.loadComments();
      }
    }

    if (name === 'lazy-load' && newValue !== oldValue) {
      // If lazy-load changes to false and we haven't loaded comments yet, load them now
      if (newValue === 'false' && this.postId && !this.commentsLoaded) {
        this.loadComments();
      }
    }
  }

  // Method to trigger lazy loading
  loadCommentsLazy() {
    if (!this.commentsLoaded && this.postId) {
      this.loadComments();
    }
  }

  // Method to set comment count from post data
  setCommentCount(count) {
    this.commentCount = count;
    this.updateCommentsCount();
  }

  setCurrentUser(user) {
    this.currentUser = user;
    this.populateAliasFields();
  }

  render() {
    this.innerHTML = `
      <div class="comments-section">
        <div class="comments-header">
          <h3>💬 Comments</h3>
          <div class="comments-count" id="commentsCount">${this.commentCount > 0 ? this.commentCount : '0'}</div>
        </div>

        <div class="comments-content">
          ${this.renderContent()}
        </div>
      </div>

    `;
  }

  renderContent() {
    // If eager loading is enabled, show loading state immediately
    const lazyLoad = this.getAttribute('lazy-load');
    const isEagerLoading = lazyLoad === 'false';

    if (this.isLoading) {
      return `
        <div class="loading-state">
          <div class="spinner"></div>
          <p>Loading comments...</p>
        </div>
      `;
    }

    // Show lazy loading state if comments haven't been loaded yet and lazy loading is enabled
    if (!this.commentsLoaded && !isEagerLoading) {
      return `
        <div class="lazy-loading-state">
          <div class="lazy-load-trigger">
            <button class="btn btn-primary load-comments-btn" id="loadCommentsBtn">
              <span class="load-icon">🎭</span>
              <span class="load-text">Load Comments (${this.commentCount})</span>
            </button>
          </div>
        </div>
      `;
    }

    // If eager loading but comments aren't loaded yet and we're not currently loading, show loading state
    if (!this.commentsLoaded && isEagerLoading) {
      return `
        <div class="loading-state">
          <div class="spinner"></div>
          <p>Loading comments...</p>
        </div>
      `;
    }

    if (this.error) {
      return `
        <div class="error-state">
          <span class="error-icon">😞</span>
          <h4>Failed to Load Comments</h4>
          <p>${this.error}</p>
        </div>
      `;
    }

    const commentsHtml = this.comments.length === 0
      ? `
        <div class="empty-state">
          <span class="empty-state-icon">💬</span>
          <h4>No comments yet</h4>
          <p>Be the first to share your thoughts!</p>
        </div>
      `
      : `
        <div class="comments-list">
          ${this.renderThreadedComments()}
        </div>
      `;

    return `
      ${commentsHtml}
      ${!this.replyingTo ? this.renderCommentForm() : ''}
    `;
  }

  renderComment(comment) {
    const createdDate = new Date(comment.createdAt);
    const timeAgo = this.formatTimeAgo(createdDate);
    const commentPermalink = this.getCommentPermalink(comment.id);

    return `
      <div class="comment-item" data-comment-id="${comment.id}">
        <a name="comment-${comment.id}"></a>
        <div class="comment-header">
          <div class="comment-author">
            <div class="comment-avatar">
              ${comment.username ? comment.username.charAt(0).toUpperCase() : '?'}
            </div>
            <div class="comment-author-info">
              <div class="comment-author-name">
                <user-mention username="${comment.username}" real-username="${comment.realUsername}" user-id="${this.currentUser.id}"  class="comment-author-link"></user-mention>
              </div>
              <div class="comment-meta">
                <span class="comment-time" title="${createdDate.toLocaleString()}">
                  ${timeAgo}
                </span>
                ${comment.isReply ? '<span>•</span><span>Reply</span>' : ''}
              </div>
            </div>
          </div>
          <div class="comment-actions">
            ${comment.isOwner ? `
              <button class="comment-action-btn" data-action="edit" data-comment-id="${comment.id}" title="Edit comment">
                ✏️
              </button>
              <button class="comment-action-btn" data-action="delete" data-comment-id="${comment.id}" title="Delete comment">
                🗑️
              </button>
            ` : `
              <button class="comment-action-btn" data-action="report" data-comment-id="${comment.id}" title="Report comment">
                🚩
              </button>
            `}
          </div>
        </div>
        
        <div class="comment-content">${comment.content_html || ''}</div>
        
        <div class="comment-footer">
          <button class="btn comment-reply-btn" data-action="reply" data-comment-id="${comment.id}">
            💬 Reply
          </button>
          <a href="${commentPermalink}" class="comment-permalink" title="Link to this comment">
            #${comment.id}
          </a>
        </div>
      </div>
    `;
  }

  renderThreadedComments() {
    // Organize comments into a tree structure
    const commentTree = this.buildCommentTree(this.comments);

    // Render the tree with proper threading
    return commentTree.map(comment => this.renderCommentThread(comment, 0)).join('');
  }

  buildCommentTree(comments) {
    const commentMap = new Map();
    const rootComments = [];

    // First pass: create a map of all comments
    comments.forEach(comment => {
      commentMap.set(comment.id, { ...comment, children: [] });
    });

    // Second pass: organize into tree structure
    comments.forEach(comment => {
      if (comment.parentId && commentMap.has(comment.parentId)) {
        // This is a reply, add it to its parent's children
        commentMap.get(comment.parentId).children.push(commentMap.get(comment.id));
      } else {
        // This is a root comment
        rootComments.push(commentMap.get(comment.id));
      }
    });

    return rootComments;
  }

  renderCommentForm(isReply = false, replyToComment = null) {
    const replyingTo = replyToComment || this.replyingTo;
    const aliasField = this.isGroupMode() ? '' : `
        <div class="alias-field">
          <label for="aliasInput" class="alias-label">
            Post as:
            <span class="mischief-icon" title="You can change your display name for this comment! Don't worry, we still know it's you 😉">
              🦝
            </span>
          </label>
          <input 
            type="text" 
            id="aliasInput" 
            name="alias" 
            class="alias-input"
            placeholder="Your display name..."
            maxlength="50"
            value="${this.currentUser && this.currentUser.username ? escapeHtml(this.currentUser.username) : ''}"
          />
        </div>
    `;

    return `
      <form class="comment-form ${isReply ? 'inline-reply-form' : ''}" id="commentForm" data-reply-to="${replyingTo ? replyingTo.id : ''}">
        ${replyingTo ? `
          <div class="comment-form-header">
            <h4 class="comment-form-title">Replying to ${escapeHtml(replyingTo.username)}</h4>
            <button type="button" class="cancel-reply-btn" id="cancelReplyBtn">Cancel</button>
          </div>
        ` : ''}
        
        ${aliasField}
        
        <markdown-preview>
          <textarea 
            id="commentTextarea" 
            name="content" 
            placeholder="${replyingTo ? 'Write your reply...' : 'Share your thoughts...'}"
            required
          ></textarea>
        </markdown-preview>
        <div class="comment-form-actions">
          <button type="submit" class="btn btn-primary" id="submitBtn">
            <span class="btn-text">${replyingTo ? 'Post Reply' : 'Post Comment'}</span>
            <span class="btn-loading" style="display: none;">⏳ Posting...</span>
          </button>
          <button type="button" class="btn btn-secondary clear-btn" id="clearBtn">Clear</button>
        </div>
      </form>
    `;
  }

  renderCommentThread(comment, depth = 0) {
    const createdDate = new Date(comment.createdAt);
    const timeAgo = this.formatTimeAgo(createdDate);
    const commentPermalink = this.getCommentPermalink(comment.id);
    const isReply = depth > 0;
    const maxDepth = 3; // Limit threading depth to prevent excessive nesting

    // Show parent context for replies
    const parentContext = isReply && comment.parentId ? this.renderParentContext(comment.parentId) : '';

    const commentHtml = `
      <div class="comment-thread" data-comment-id="${comment.id}" data-depth="${depth}">
        <div class="comment-item ${isReply ? 'comment-reply' : 'comment-root'}" style="--thread-depth: ${Math.min(depth, maxDepth)}" data-depth="${depth}">
          ${parentContext}
          <div class="comment-main">
            <a name="comment-${comment.id}"></a>
            <div class="comment-header">
              <div class="comment-author">
                <div class="comment-avatar">
                  ${comment.username ? comment.username.charAt(0).toUpperCase() : '?'}
                </div>
                <div class="comment-author-info">
                  <div class="comment-author-name">
                    <user-mention username="${comment.username}" real-username="${comment.realUsername}" user-id="${this.currentUser?.id}" class="comment-author-link"></user-mention>
                  </div>
                  <div class="comment-meta">
                    <span class="comment-time" title="${createdDate.toLocaleString()}">
                      ${timeAgo}
                    </span>
                    ${isReply ? '<span class="reply-indicator">• Reply</span>' : ''}
                  </div>
                </div>
              </div>
              <div class="comment-actions">
                ${comment.isOwner ? `
                  <button class="comment-action-btn" data-action="edit" data-comment-id="${comment.id}" title="Edit comment">
                    ✏️
                  </button>
                  <button class="comment-action-btn" data-action="delete" data-comment-id="${comment.id}" title="Delete comment">
                    🗑️
                  </button>
                ` : `
                  <button class="comment-action-btn" data-action="report" data-comment-id="${comment.id}" title="Report comment">
                    🚩
                  </button>
                `}
              </div>
            </div>
            
            ${this.editingCommentId === comment.id ? this.renderEditForm(comment) : `
              <div class="comment-content">${comment.content_html || ''}</div>
              
              <div class="comment-footer">
                <button class="btn comment-reply-btn" data-action="reply" data-comment-id="${comment.id}">
                  💬 Reply
                </button>
                <a href="${commentPermalink}" class="comment-permalink" title="Link to this comment">
                  #${comment.id}
                </a>
              </div>
            `}
          </div>
        </div>
        
        ${this.replyingTo && this.replyingTo.id === comment.id ? `
          <div class="inline-reply-container">
            ${this.renderCommentForm(true, comment)}
          </div>
        ` : ''}
        
        ${comment.children && comment.children.length > 0 ? `
          <div class="comment-replies">
            ${comment.children.map(child => this.renderCommentThread(child, depth + 1)).join('')}
          </div>
        ` : ''}
      </div>
    `;

    return commentHtml;
  }

  renderParentContext(parentId) {
    const parentComment = this.comments.find(c => c.id === parentId);
    if (!parentComment) return '';

    return `
      <div class="parent-context">
        <div class="parent-context-line"></div>
        <div class="parent-context-content">
          <span class="parent-context-author">@${escapeHtml(parentComment.username)}</span>
          <span class="parent-context-text">${this.truncateText(parentComment.content, 50)}</span>
        </div>
      </div>
    `;
  }

  truncateText(text, maxLength) {
    if (text.length <= maxLength) return escapeHtml(text);
    return escapeHtml(text.substring(0, maxLength)) + '...';
  }

  attachEventListeners() {
    // Remove existing listeners to prevent duplicates
    this.removeEventListeners();

    // Comment form submission - handle all forms
    const commentForms = this.querySelectorAll('.comment-form');
    commentForms.forEach(form => {
      form.addEventListener('submit', this.handleFormSubmit);
    });

    // Edit form submission - handle all edit forms
    const editForms = this.querySelectorAll('.edit-comment-form');
    editForms.forEach(form => {
      form.addEventListener('submit', this.handleEditFormSubmit);
    });

    // Clear button - clears the comment form
    const clearBtns = this.querySelectorAll('#clearBtn');
    clearBtns.forEach(btn => {
      btn.addEventListener('click', this.handleClearClick);
    });

    // Cancel buttons - handle cancel reply and edit cancel buttons
    const cancelBtns = this.querySelectorAll('#cancelReplyBtn, #editCancelBtn');
    cancelBtns.forEach(btn => {
      btn.addEventListener('click', this.handleCancelClick);
    });

    // Load comments button
    const loadCommentsBtn = this.querySelector('#loadCommentsBtn');
    if (loadCommentsBtn) {
      loadCommentsBtn.addEventListener('click', this.handleLoadComments);
    }

    // Comment actions - delegated click handler (stable reference for add/remove)
    this.addEventListener('click', this.handleCommentActionClick);

    // Character count for edit forms
    const editTextareas = this.querySelectorAll('#editTextarea');
    editTextareas.forEach(textarea => {
      const charCount = textarea.parentElement.querySelector('#editCharCount');
      if (charCount) {
        textarea.addEventListener('input', () => {
          charCount.textContent = textarea.value.length;
        });
      }
    });
  }

  removeEventListeners() {
    // Remove all form event listeners
    const commentForms = this.querySelectorAll('.comment-form');
    commentForms.forEach(form => {
      form.removeEventListener('submit', this.handleFormSubmit);
    });

    const editForms = this.querySelectorAll('.edit-comment-form');
    editForms.forEach(form => {
      form.removeEventListener('submit', this.handleEditFormSubmit);
    });

    const clearBtns = this.querySelectorAll('#clearBtn');
    clearBtns.forEach(btn => {
      btn.removeEventListener('click', this.clearForm);
    });

    const cancelBtns = this.querySelectorAll('#cancelReplyBtn, #editCancelBtn');
    cancelBtns.forEach(btn => {
      btn.removeEventListener('click', this.handleCancelClick);
    });

    const loadCommentsBtn = this.querySelector('#loadCommentsBtn');
    if (loadCommentsBtn) {
      loadCommentsBtn.removeEventListener('click', this.handleLoadComments);
    }

    this.removeEventListener('click', this.handleCommentActionClick);
  }

  handleCommentActionClick = (e) => {
    const actionEl = e.target.closest('[data-action]');
    if (!actionEl || !this.contains(actionEl)) {
      return;
    }

    const action = actionEl.dataset.action;
    const commentId = actionEl.dataset.commentId;

    switch (action) {
    case 'reply':
      this.startReply(commentId);
      break;
    case 'edit':
      this.editComment(commentId);
      break;
    case 'delete':
      this.deleteComment(commentId);
      break;
    case 'report':
      e.preventDefault();
      e.stopPropagation();
      this.reportComment(commentId);
      break;
    }
  };

  // Bound event handlers to prevent memory leaks and allow removal
  handleFormSubmit = (e) => {
    e.preventDefault();
    this.handleSubmit(e.target);
  };

  handleEditFormSubmit = (e) => {
    e.preventDefault();
    const commentId = e.target.id.replace('edit-form-', '');
    this.handleEditSubmit(commentId);
  };

  handleClearClick = () => {
    this.clearForm();
  };

  handleCancelClick = (e) => {
    if (e.target.id === 'cancelReplyBtn') {
      this.cancelReply();
    } else if (e.target.id === 'editCancelBtn') {
      this.cancelEdit();
    }
  };

  handleLoadComments = () => {
    this.loadCommentsLazy();
  };

  populateAliasFields() {
    const aliasInputs = this.querySelectorAll('.alias-input');
    aliasInputs.forEach(input => {
      if (this.currentUser && this.currentUser.username) {
        input.value = this.currentUser.username;
      }
    });
  }

  async loadComments() {
    if (!this.postId) return;

    this.isLoading = true;
    this.error = null;
    this.commentsLoaded = true;
    this.updateContent();

    try {
      const response = this.isGroupMode()
        ? await window.API.request(`/groups/${this.groupId}/posts/${this.postId}/comments`)
        : await window.API.request(`/comments/post/${this.postId}`);
      this.comments = response.comments || [];
      this.updateCommentsCount();
    } catch (error) {
      window.logger.error('Failed to load comments:', error);
      this.comments = [];
      this.error = error.message || 'Failed to load comments';
    } finally {
      this.isLoading = false;
      this.updateContent();
    }
  }

  async updateContent() {
    const contentContainer = this.querySelector('.comments-content');
    if (contentContainer) {
      contentContainer.innerHTML = this.renderContent();
      this.attachEventListeners();
      this.populateAliasFields();

      // Note: Images already have proxy URLs from backend (content_html field)
      // Only process if content_html is missing (fallback for old data)
      if (this.currentUser?.id) {
        const postContext = this.isGroupMode() ? 'group' : 'friend';
        const needsProcessing = this.comments.some(comment => comment.content && !comment.content_html);
        if (needsProcessing) {
          try {
            const { processImageProxyUrls } = await import('../utils/image-proxy-processor.js');
            const processedHtml = await processImageProxyUrls(
              contentContainer.innerHTML,
              this.currentUser.id,
              this.postId || null,
              postContext
            );
            contentContainer.innerHTML = processedHtml;
          } catch (error) {
            window.logger.warn('Failed to process image proxy URLs in comments:', error);
          }
        }

        try {
          const { attachProxyImageRemintHandlers } = await import('../utils/image-proxy-processor.js');
          attachProxyImageRemintHandlers(contentContainer, {
            postId: this.postId ? parseInt(this.postId, 10) : null,
            postContext
          });
        } catch (error) {
          window.logger.warn('Failed to attach proxy image remint handlers:', error);
        }
      }
    }
  }

  updateCommentsCount() {
    const countElement = this.querySelector('#commentsCount');
    if (countElement) {
      const count = this.comments.length;
      countElement.textContent = count === 1 ? '1 comment' : `${count} comments`;
    }
  }

  async handleSubmit(form = null) {
    // If no form provided, try to find the active form
    if (!form) {
      form = this.querySelector('.comment-form');
    }

    const textarea = form.querySelector('#commentTextarea');
    const aliasInput = form.querySelector('#aliasInput');
    const submitBtn = form.querySelector('#submitBtn');
    const content = textarea.value.trim();
    const alias = aliasInput?.value?.trim() || '';

    if (!content) return;

    // Use alias if provided, otherwise fall back to current username
    const displayName = alias || (this.currentUser ? this.currentUser.username : 'Anonymous');

    submitBtn.classList.add('loading');
    submitBtn.disabled = true;

    // Determine if this is a reply based on the form's data attribute
    const replyToId = form.dataset.replyTo;
    const isReply = replyToId && replyToId !== '';

    try {
      let response;
      if (this.isGroupMode()) {
        response = await window.API.request(
          `/groups/${this.groupId}/posts/${this.postId}/comments`,
          {
            method: 'POST',
            body: JSON.stringify({
              content_markdown: content,
              ...(isReply && { parent_comment_id: parseInt(replyToId, 10) })
            })
          }
        );
      } else {
        const requestBody = {
          postId: parseInt(this.postId),
          content: content,
          displayName: displayName,
          ...(isReply && { parentId: parseInt(replyToId) })
        };
        response = await window.API.request('/comments', {
          method: 'POST',
          body: JSON.stringify(requestBody)
        });
      }

      this.showSuccess(isReply ? 'Reply posted!' : 'Comment posted!');
      this.clearForm();
      await this.loadComments();

      // Dispatch event for other components
      this.dispatchEvent(new CustomEvent('comment-added', {
        detail: { comment: response.comment || response },
        bubbles: true
      }));

    } catch (error) {
      window.logger.error('Failed to post comment:', error);
      this.showError(error.message || 'Failed to post comment');
    } finally {
      submitBtn.classList.remove('loading');
      submitBtn.disabled = false;
    }
  }

  startReply(commentId) {
    // Prevent replying if already editing a comment
    if (this.editingCommentId) {
      this.showError('Please finish or cancel your edit before replying to a comment.');
      return;
    }

    const comment = this.comments.find(c => c.id === parseInt(commentId));
    if (comment) {
      this.replyingTo = comment;
      this.updateContent();

      const textarea = this.querySelector('#commentTextarea');
      textarea?.focus();
    }
  }

  cancelReply() {
    this.replyingTo = null;
    this.updateContent();
  }

  clearForm() {
    this.replyingTo = null;
    const textarea = this.querySelector('#commentTextarea');
    if (textarea) {
      textarea.value = '';
    }
    this.updateContent();
  }

  editComment(commentId) {
    // Convert string ID to number for comparison
    const numericCommentId = parseInt(commentId, 10);

    window.logger.component('edit_start', 'comments-section', { commentId: numericCommentId });

    // Prevent editing if already replying to a comment
    if (this.replyingTo) {
      this.showError('Please finish or cancel your reply before editing a comment.');
      return;
    }

    // Find the comment to edit
    const comment = this.findCommentById(numericCommentId);
    if (!comment) {
      window.logger.error('Comment not found for editing', null, {
        commentId: numericCommentId,
        availableIds: this.comments.map(c => c.id),
        totalComments: this.comments.length
      });
      this.showError('Comment not found.');
      return;
    }

    // Set editing state
    this.editingCommentId = numericCommentId;
    this.updateContent();
  }

  // Helper method to find a comment by ID (including nested replies)
  findCommentById(commentId, comments = this.comments) {
    for (const comment of comments) {
      if (comment.id === commentId) {
        return comment;
      }
      // Check replies recursively
      if (comment.replies && comment.replies.length > 0) {
        const found = this.findCommentById(commentId, comment.replies);
        if (found) return found;
      }
    }
    return null;
  }

  // Handle edit form submission
  async handleEditSubmit(commentId) {
    // Convert string ID to number
    const numericCommentId = parseInt(commentId, 10);

    const editForm = this.querySelector(`#edit-form-${numericCommentId}`);
    if (!editForm) return;

    const textarea = editForm.querySelector('#editTextarea');
    const submitBtn = editForm.querySelector('#editSubmitBtn');
    const content = textarea.value.trim();

    if (!content) {
      this.showError('Comment content cannot be empty.');
      return;
    }

    if (content.length > 1000) {
      this.showError('Comment content must be less than 1000 characters.');
      return;
    }

    submitBtn.classList.add('loading');
    submitBtn.disabled = true;

    try {
      let response;
      if (this.isGroupMode()) {
        response = await window.API.request(
          `/groups/${this.groupId}/posts/${this.postId}/comments/${numericCommentId}`,
          {
            method: 'PUT',
            body: JSON.stringify({ content_markdown: content })
          }
        );
      } else {
        response = await window.API.request(`/comments/${numericCommentId}`, {
          method: 'PUT',
          body: JSON.stringify({ content })
        });
      }

      window.logger.component('edit_success', 'comments-section', { commentId: numericCommentId });
      this.showSuccess('Comment updated successfully!');

      // Exit edit mode and reload comments
      this.editingCommentId = null;
      await this.loadComments();

      // Dispatch event for other components
      this.dispatchEvent(new CustomEvent('comment-updated', {
        detail: { comment: response.comment || response },
        bubbles: true
      }));

    } catch (error) {
      window.logger.error('Failed to update comment', error, { commentId: numericCommentId });
      this.showError(error.message || 'Failed to update comment');
    } finally {
      submitBtn.classList.remove('loading');
      submitBtn.disabled = false;
    }
  }

  // Cancel edit mode
  cancelEdit() {
    window.logger.component('edit_cancel', 'comments-section', { commentId: this.editingCommentId });
    this.editingCommentId = null;
    this.updateContent();
  }

  // Render edit form for a comment
  renderEditForm(comment) {
    return `
      <form class="edit-comment-form" id="edit-form-${comment.id}">
        <div class="edit-form-group">
          <textarea 
            id="editTextarea" 
            class="edit-comment-textarea"
            placeholder="Edit your comment..."
            maxlength="1000"
            required
          >${escapeHtml(comment.content)}</textarea>
          <div class="edit-form-actions">
            <button type="submit" id="editSubmitBtn" class="edit-submit-btn">
              💾 Save
            </button>
            <button type="button" id="editCancelBtn" class="edit-cancel-btn">
              ❌ Cancel
            </button>
          </div>
          <div class="char-count">
            <span id="editCharCount">${comment.content.length}</span>/1000 characters
          </div>
        </div>
      </form>
    `;
  }

  async deleteComment(commentId) {
    // Convert string ID to number
    const numericCommentId = parseInt(commentId, 10);

    if (!confirm('Are you sure you want to delete this comment? This action cannot be undone.')) {
      return;
    }

    window.logger.component('delete_start', 'comments-section', { commentId: numericCommentId });

    try {
      if (this.isGroupMode()) {
        await window.API.request(
          `/groups/${this.groupId}/posts/${this.postId}/comments/${numericCommentId}`,
          { method: 'DELETE' }
        );
      } else {
        await window.API.request(`/comments/${numericCommentId}`, {
          method: 'DELETE'
        });
      }

      window.logger.component('delete_success', 'comments-section', { commentId: numericCommentId });
      this.showSuccess('Comment deleted successfully!');

      // Reload comments to reflect the deletion
      await this.loadComments();

      // Dispatch event for other components
      this.dispatchEvent(new CustomEvent('comment-deleted', {
        detail: { commentId: numericCommentId },
        bubbles: true
      }));

    } catch (error) {
      window.logger.error('Failed to delete comment', error, { commentId: numericCommentId });
      this.showError(error.message || 'Failed to delete comment');
    }
  }

  formatTimeAgo(date) {
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) {
      return 'Just now';
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes}m ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours}h ago`;
    } else if (diffInSeconds < 2592000) {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days}d ago`;
    } else {
      return date.toLocaleDateString();
    }
  }

  showSuccess(message) {
    const successDiv = document.createElement('div');
    successDiv.className = 'alert alert-success';
    successDiv.textContent = message;
    successDiv.style.position = 'fixed';
    successDiv.style.top = '20px';
    successDiv.style.right = '20px';
    successDiv.style.zIndex = '10000';
    successDiv.style.animation = 'fadeIn 0.3s ease-in-out';

    document.body.appendChild(successDiv);

    setTimeout(() => successDiv.remove(), 4000);
  }

  showError(message) {
    const errorDiv = document.createElement('div');
    errorDiv.className = 'alert alert-error';
    errorDiv.textContent = message;
    errorDiv.style.position = 'fixed';
    errorDiv.style.top = '20px';
    errorDiv.style.right = '20px';
    errorDiv.style.zIndex = '10000';
    errorDiv.style.animation = 'fadeIn 0.3s ease-in-out';

    document.body.appendChild(errorDiv);

    setTimeout(() => errorDiv.remove(), 4000);
  }

  async reportComment(commentId) {
    if (this._reportInFlight) {
      return;
    }

    const numericCommentId = parseInt(commentId, 10);
    const comment = this.findCommentById(numericCommentId);

    if (!comment) {
      window.logger.error('Comment not found for reporting', null, { commentId: numericCommentId });
      alert('Comment not found.');
      return;
    }

    this._reportInFlight = true;
    try {
      const reason = prompt('Why are you reporting this comment?\n\nPlease provide a brief reason (e.g., "Inappropriate content", "Spam", "Harassment", etc.):');

      if (!reason || reason.trim().length === 0) {
        return;
      }

      const description = prompt('Additional details (optional):\n\nProvide any additional context that might help us understand the issue:') || '';

      if (this.isGroupMode()) {
        await window.API.request(`/groups/${this.groupId}/reports`, {
          method: 'POST',
          body: JSON.stringify({
            target_type: 'comment',
            target_id: numericCommentId,
            reason: reason.trim(),
            details: `Comment #${numericCommentId}: ${description.trim()}`
          })
        });
        alert('Thank you for reporting this comment. Group moderators will review it.');
        return;
      }

      const response = await window.API.request('/support/flag-post', {
        method: 'POST',
        body: JSON.stringify({
          post_id: this.postId,
          comment_id: numericCommentId,
          reason: reason.trim(),
          description: `Comment #${numericCommentId}: ${description.trim()}\n\nComment content: ${comment.content.substring(0, 200)}${comment.content.length > 200 ? '...' : ''}`
        })
      });

      alert('Thank you for reporting this comment. Our team will review it shortly.');

      const safetyArmadillo = document.querySelector('safety-armadillo');
      if (safetyArmadillo && response.ticket) {
        safetyArmadillo.open();
        setTimeout(() => {
          safetyArmadillo.viewTicket(response.ticket.id || response.ticket.conversation_id);
        }, 500);
      }
    } catch (error) {
      window.logger.error('Failed to report comment:', error);
      alert(`Failed to report comment: ${error.message || 'Please try again later.'}`);
    } finally {
      this._reportInFlight = false;
    }
  }
}

// Register the custom element
customElements.define('comments-section', CommentsSection);


